import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartBar,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  Brain,
  ChartLineUp,
  Target,
  Question,
  CaretDown,
  Eye,
  Lightbulb,
  BookOpen,
  ArrowsClockwise,
  Funnel,
  Clock,
  Calculator,
  Shuffle,
  CurrencyBtc,
  Crosshair,
  ListChecks,
  ShieldCheck,
  Timer,
  Repeat
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const HowManyTradesToValidate = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'How Many Trades to Validate a Strategy | Trading Guide',
      description: 'Learn how many trades you need to validate a trading strategy and avoid common mistakes caused by small sample sizes.',
      path: '/how-many-trades-to-validate-a-strategy',
      lang: 'en',
      alternates: getArticleAlternates('/how-many-trades-to-validate-a-strategy'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "How many trades is statistically significant?",
      answer: "In traditional statistics, 30 samples is often cited as a minimum threshold for the Central Limit Theorem to apply. However, in trading, 30 trades is barely a starting point. For meaningful confidence, most professional traders and quantitative analysts recommend 100+ trades minimum, ideally 200-500 for robust validation. The higher variance in trading outcomes (compared to controlled experiments) means you need more data to distinguish skill from luck."
    },
    {
      question: "Can you trust a strategy after 20 trades?",
      answer: "No. Twenty trades is statistically meaningless for validation. A coin flip could easily produce 14 wins out of 20 (70% win rate) or 8 wins (40% win rate) purely by chance. With 20 trades, you cannot distinguish between a genuinely profitable strategy and random variance. Use 20 trades to identify obvious flaws, but never to confirm a strategy works."
    },
    {
      question: "Why do strategies fail after working initially?",
      answer: "Several factors cause this: 1) Small sample size created false confidence—the initial success was luck, not edge. 2) Market conditions changed—a trend-following strategy that worked in a bull market fails in ranging conditions. 3) Curve fitting—the strategy was optimized for past data and doesn't generalize. 4) Execution drift—emotions caused deviation from the original rules. This is why validation requires testing across different market conditions, not just during favorable periods."
    },
    {
      question: "Does backtesting replace real trading data?",
      answer: "No. Backtesting is valuable for initial screening—eliminating strategies that clearly don't work historically. But it cannot replicate real trading conditions: slippage, emotional pressure, execution timing, and liquidity issues. A strategy that shows 65% win rate in backtesting might achieve only 50% in live trading due to these factors. Use backtesting to filter, then validate with real (or paper) trading data."
    },
    {
      question: "Should I validate on one asset or multiple?",
      answer: "Start with one asset or a homogeneous group of assets. Mixing BTC, ETH, and random altcoins in the same validation pool corrupts your data—each asset has different characteristics. Once validated on one asset, you can test whether the strategy generalizes to similar assets. A strategy that works on BTC may need different parameters for low-cap altcoins due to liquidity and volatility differences."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Try GENI Free
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <Calculator size={16} weight="fill" />
            <span>Trading Statistics</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            How Many Trades Do You Need to Validate a Trading Strategy?
          </h1>
          
          <p className="text-lg text-white/70 leading-relaxed">
            "I tested my strategy for two weeks and it's profitable!" Sound familiar? This is 
            one of the most dangerous statements in trading. Judging a strategy based on a handful 
            of trades is like flipping a coin ten times, getting seven heads, and concluding the 
            coin is biased.
          </p>
        </header>

        {/* Introduction */}
        <section className="mb-14 prose prose-invert max-w-none">
          <p className="text-white/70 leading-relaxed text-lg">
            <strong className="text-white">Small sample sizes create illusions.</strong> A strategy 
            might win 8 out of 10 trades and feel unstoppable—then lose the next 15. Was the 
            strategy bad? Or was the initial success just luck? Without enough data, you can't 
            know. And acting on incomplete data leads to abandoning good strategies too early or 
            trusting bad ones too long.
          </p>
          <p className="text-white/70 leading-relaxed text-lg">
            Understanding <strong className="text-white">how many trades to validate a strategy</strong> isn't 
            about finding a magic number—it's about respecting probability and building genuine 
            confidence through sufficient evidence.
          </p>
        </section>

        {/* Section 1: Why Most Traders Validate Incorrectly */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-red-400 text-lg font-mono">01</span>
            Why Most Traders Validate Strategies Incorrectly
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              The human brain is wired to find patterns—even where none exist. After a few 
              winning trades, we feel we've cracked the code. After a few losses, we're ready 
              to throw everything away. Neither reaction is rational.
            </p>
          </div>
          
          <div className="space-y-4">
            {[
              {
                title: "Relying on 5–10 Trades",
                description: "This is statistically meaningless. Ten trades cannot distinguish between skill and random chance. A 70% win rate over 10 trades could easily become 45% over the next 50.",
                icon: Warning
              },
              {
                title: "Emotional Bias After Wins",
                description: "Three winners in a row create overconfidence. You increase position size, skip your checklist, trade setups that don't fully qualify—then wonder why results collapse.",
                icon: TrendUp
              },
              {
                title: "Panic After Losses",
                description: "Four consecutive losses feel like proof the strategy is broken. But even a 60% win rate strategy will have losing streaks. Quitting during a normal drawdown means never seeing the recovery.",
                icon: ChartLineUp
              },
              {
                title: "Overconfidence or Panic",
                description: "Both extremes stem from the same error: treating small samples as definitive. Validation requires emotional neutrality and patience—neither celebrating too early nor abandoning too quickly.",
                icon: Brain
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-red-500/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 2: The Importance of Sample Size */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">02</span>
            The Importance of Sample Size
          </h2>
          
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/20 rounded-lg p-8 mb-6">
            <p className="text-white/80 leading-relaxed mb-4">
              <strong className="text-white">Trading sample size</strong> determines whether your 
              conclusions are real or illusion. Small samples are dominated by randomness; large 
              samples reveal the true underlying pattern.
            </p>
            <p className="text-white/70 leading-relaxed">
              This is basic probability theory: as sample size increases, results converge toward 
              the "true" expected value. With 10 trades, you might see anything. With 200 trades, 
              patterns stabilize.
            </p>
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Consider a strategy with a true 55% win rate. In 10 trades, you could easily see 
              80% wins (luck) or 30% wins (bad luck). In 100 trades, results will cluster closer 
              to 55%. In 500 trades, you'll have high confidence the rate is genuinely around 55%.
            </p>
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">Why does this matter?</strong> Because you make 
              decisions based on what you observe. If you observe 80% and believe it, you'll 
              over-leverage. If you observe 30% and believe it, you'll quit a winning strategy. 
              Both errors come from trusting insufficient data.
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Shuffle size={18} className="text-purple-400" />
              Randomness in Trading Outcomes
            </h4>
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              Even the best strategy has randomness built in. Market conditions, timing, 
              liquidity—all introduce variance. A 60% edge doesn't mean 6 wins out of every 10. 
              It means over hundreds of trades, you'll average around 60%.
            </p>
            <p className="text-white/60 text-sm leading-relaxed">
              Accepting this randomness is psychologically difficult but mathematically necessary. 
              Your job isn't to win every trade—it's to execute consistently and let the edge 
              compound over sufficient volume.
            </p>
          </div>
        </section>

        {/* Section 3: What Is a "Good" Number of Trades? */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">03</span>
            What Is a "Good" Number of Trades?
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              There's no single magic number, but there are useful thresholds for thinking about 
              <strong className="text-white"> trading statistics</strong> and confidence levels:
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                trades: "10-30",
                label: "Early Signal Only",
                description: "Useful for identifying obvious flaws—if a strategy loses 90% of trades, you know quickly. But winning at this stage proves nothing. Don't trust it.",
                color: "red",
                confidence: "Very Low"
              },
              {
                trades: "30-50",
                label: "Emerging Pattern",
                description: "Patterns begin to appear, but variance is still high. A 60% win rate here could easily be 50% in reality. Continue testing without drawing firm conclusions.",
                color: "orange",
                confidence: "Low"
              },
              {
                trades: "50-100",
                label: "Meaningful Data",
                description: "This is where validation actually starts. Results become more reliable. If performance is consistent across 100 trades, you have reasonable (not definitive) confidence.",
                color: "yellow",
                confidence: "Moderate"
              },
              {
                trades: "100-200",
                label: "Strong Foundation",
                description: "Solid validation range. If your strategy maintains positive expectancy over 150+ trades across varied conditions, you have real evidence of an edge.",
                color: "green",
                confidence: "Good"
              },
              {
                trades: "200+",
                label: "High Confidence",
                description: "Professional-level validation. At this volume, you can trust your metrics. Win rate, profit factor, and drawdown patterns are reliable indicators of true performance.",
                color: "emerald",
                confidence: "High"
              }
            ].map((item, index) => (
              <div 
                key={index}
                className={`bg-[#121212] border rounded-lg p-6 ${
                  item.color === 'red' ? 'border-red-500/30' :
                  item.color === 'orange' ? 'border-orange-500/30' :
                  item.color === 'yellow' ? 'border-yellow-500/30' :
                  item.color === 'green' ? 'border-green-500/30' :
                  'border-emerald-500/30'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl font-bold ${
                      item.color === 'red' ? 'text-red-400' :
                      item.color === 'orange' ? 'text-orange-400' :
                      item.color === 'yellow' ? 'text-yellow-400' :
                      item.color === 'green' ? 'text-green-400' :
                      'text-emerald-400'
                    }`}>{item.trades}</span>
                    <span className="text-white/50 text-sm">trades</span>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded ${
                    item.color === 'red' ? 'bg-red-500/20 text-red-400' :
                    item.color === 'orange' ? 'bg-orange-500/20 text-orange-400' :
                    item.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-400' :
                    item.color === 'green' ? 'bg-green-500/20 text-green-400' :
                    'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    {item.confidence} Confidence
                  </span>
                </div>
                <h4 className="font-semibold mb-2">{item.label}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-lg p-6">
            <p className="text-white/80 text-sm leading-relaxed">
              <strong className="text-white">Key principle:</strong> More data is always better. 
              There's no point at which you have "enough" data and can stop learning. The traders 
              who consistently improve are the ones who never stop tracking and analyzing.
            </p>
          </div>
        </section>

        {/* Section 4: The Role of Market Conditions */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">04</span>
            The Role of Market Conditions
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              A strategy validated during a bull market isn't validated at all—it's validated 
              <em className="text-white/90"> for bull markets</em>. True validation requires 
              testing across different market environments.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              {
                condition: "Trending",
                description: "Momentum strategies thrive. Mean reversion struggles. Breakouts work well.",
                icon: TrendUp
              },
              {
                condition: "Ranging",
                description: "Support/resistance plays work. Breakouts fail repeatedly. Patience required.",
                icon: ArrowsClockwise
              },
              {
                condition: "Volatile",
                description: "Wide stops needed. Quick profits possible. Higher risk of stop hunts.",
                icon: ChartBar
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#121212] border border-white/10 rounded-lg p-5">
                <item.icon size={24} weight="duotone" className="text-[#00E676] mb-3" />
                <h4 className="font-semibold mb-2">{item.condition}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">Crypto volatility</strong> adds another layer. A 
              strategy tested only during a calm accumulation phase will behave completely 
              differently during a parabolic run or a crash. Your 100 trades need to include 
              varied conditions—otherwise you're not validating the strategy, just validating 
              it works when conditions are favorable.
            </p>
          </div>
        </section>

        {/* Section 5: The Danger of Mixing Assets */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-orange-400 text-lg font-mono">05</span>
            The Danger of Mixing Different Assets
          </h2>
          
          <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-8 mb-6">
            <p className="text-white/80 leading-relaxed">
              Combining trades from BTC, ETH, SOL, and random low-cap altcoins into one validation 
              pool is a recipe for misleading conclusions. Each asset class behaves differently—mixing 
              them corrupts your <strong className="text-white">trading statistics</strong>.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <CurrencyBtc size={18} className="text-[#F7931A]" />
                High-Cap Assets (BTC, ETH)
              </h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  High liquidity, tight spreads
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  More predictable price action
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  Technical levels respected more often
                </li>
              </ul>
            </div>
            
            <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Shuffle size={18} className="text-purple-400" />
                Low-Cap Altcoins
              </h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li className="flex items-start gap-2">
                  <Warning size={14} weight="fill" className="text-orange-400 mt-0.5 flex-shrink-0" />
                  Thin order books, high slippage
                </li>
                <li className="flex items-start gap-2">
                  <Warning size={14} weight="fill" className="text-orange-400 mt-0.5 flex-shrink-0" />
                  Prone to manipulation
                </li>
                <li className="flex items-start gap-2">
                  <Warning size={14} weight="fill" className="text-orange-400 mt-0.5 flex-shrink-0" />
                  False breakouts common
                </li>
              </ul>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              A breakout strategy showing 60% win rate might actually be: 75% on BTC, 55% on ETH, 
              and 35% on altcoins. The aggregate hides the fact that you should only trade this 
              setup on majors. Learning <Link to="/how-to-find-a-trading-setup" className="text-[#00E676] hover:underline">how to find a profitable trading setup</Link> means 
              understanding these asset-specific differences.
            </p>
          </div>
        </section>

        {/* Section 6: Why Backtesting Alone Is Not Enough */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">06</span>
            Why Backtesting Alone Is Not Enough
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              TradingView backtesting and historical chart analysis are valuable—they help filter 
              out strategies that clearly don't work. But backtesting cannot replicate the 
              reality of live trading.
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                gap: "Execution Slippage",
                backtest: "Perfect fills at exact price",
                reality: "Slippage on entries and exits, especially in volatile conditions",
                icon: Timer
              },
              {
                gap: "Emotional Pressure",
                backtest: "Decisions made calmly after the fact",
                reality: "Real money triggers fear, greed, and hesitation",
                icon: Brain
              },
              {
                gap: "Timing Issues",
                backtest: "Entry at candle close assumed",
                reality: "You might enter early, late, or miss the setup entirely",
                icon: Clock
              },
              {
                gap: "Liquidity Reality",
                backtest: "Unlimited position size assumed",
                reality: "Large orders move price; small coins have thin books",
                icon: ChartBar
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <item.icon size={20} weight="duotone" className="text-[#00E676]" />
                  <h4 className="font-semibold">{item.gap}</h4>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-green-500/10 rounded">
                    <p className="text-green-400 text-xs font-medium mb-1">Backtest</p>
                    <p className="text-white/60">{item.backtest}</p>
                  </div>
                  <div className="p-3 bg-orange-500/10 rounded">
                    <p className="text-orange-400 text-xs font-medium mb-1">Reality</p>
                    <p className="text-white/60">{item.reality}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">Use backtesting to filter, not to validate.</strong> If 
              a strategy fails in backtesting, it will almost certainly fail live. But success in 
              backtesting only means the strategy is worth testing with real data—it doesn't 
              guarantee live profitability.
            </p>
          </div>
        </section>

        {/* Section 7: The Psychological Trap */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-purple-400 text-lg font-mono">07</span>
            The Psychological Trap
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Psychology sabotages validation more than statistics do. Even traders who 
              intellectually understand sample size requirements make emotional decisions based 
              on short-term results.
            </p>
          </div>
          
          <div className="bg-[#121212] border border-purple-500/20 rounded-lg p-8 mb-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2 text-purple-400">
              <Brain size={20} weight="duotone" />
              Common Psychological Traps
            </h3>
            <div className="space-y-4">
              {[
                {
                  trap: "Quitting Too Early After Losses",
                  explanation: "Five losses in a row feel like proof the strategy is broken. But even a 55% strategy will have 5-loss streaks regularly. Quitting mid-drawdown means never seeing the recovery."
                },
                {
                  trap: "Trusting Too Early After Wins",
                  explanation: "Seven winners feel like confirmation. Position sizes increase. Risk management loosens. Then a normal losing streak wipes out all the gains—and more."
                },
                {
                  trap: "Strategy Hopping",
                  explanation: "Abandoning strategies before they're validated, always searching for something 'better.' This guarantees you never develop real confidence in any approach."
                }
              ].map((item, index) => (
                <div key={index} className="border-l-2 border-purple-400/50 pl-4">
                  <h4 className="font-medium text-sm mb-1">{item.trap}</h4>
                  <p className="text-white/60 text-sm leading-relaxed">{item.explanation}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <BookOpen size={24} weight="duotone" className="text-purple-400 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-2">On Probability Thinking</h4>
                <p className="text-white/70 text-sm leading-relaxed">
                  In <strong className="text-white">"Trading in the Zone,"</strong> Mark Douglas 
                  emphasizes that consistent trading requires accepting uncertainty at the 
                  individual trade level while trusting probability over many trades. This mental 
                  shift—from outcome-focused to process-focused—is essential for proper validation. 
                  You can't validate a strategy if you panic after every loss or celebrate after 
                  every win.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 8: What You Should Track */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">08</span>
            What You Should Track While Validating
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Raw win/loss counts aren't enough. To properly <strong className="text-white">validate 
              a trading strategy</strong>, you need to track multiple metrics that together reveal 
              the true nature of your edge.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              {
                metric: "Win Rate",
                description: "Percentage of profitable trades. Important but incomplete without R:R context.",
                icon: Target
              },
              {
                metric: "Risk/Reward Ratio",
                description: "Average gain on winners vs. average loss on losers. Determines how win rate translates to profit.",
                icon: ChartLineUp
              },
              {
                metric: "Profit Factor",
                description: "Gross profit ÷ gross loss. Above 1.5 suggests a meaningful edge. Above 2.0 is excellent.",
                icon: Calculator
              },
              {
                metric: "Consistency",
                description: "Are results stable week-to-week? High variance suggests luck or changing conditions.",
                icon: Repeat
              },
              {
                metric: "Execution Quality",
                description: "Did you follow the rules? A strategy isn't validated if you're not trading it properly.",
                icon: Crosshair
              },
              {
                metric: "Max Drawdown",
                description: "Worst peak-to-trough decline. Can you handle this psychologically and financially?",
                icon: ChartBar
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-5"
              >
                <div className="flex items-center gap-3 mb-2">
                  <item.icon size={18} weight="duotone" className="text-[#00E676]" />
                  <h4 className="font-semibold">{item.metric}</h4>
                </div>
                <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Understanding <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline">how to analyze your trades</Link> properly 
              means looking at all these metrics together. A high win rate with poor R:R might 
              still be unprofitable. A low win rate with excellent R:R could be highly profitable. 
              Context matters.
            </p>
          </div>
        </section>

        {/* Section 9: The Real Goal */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">09</span>
            The Real Goal: Confidence Through Data
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Why does validation matter? Because <strong className="text-white">confidence is the 
              foundation of consistent execution.</strong> Without genuine confidence in your 
              strategy, you'll second-guess entries, exit early, skip setups, and eventually 
              abandon the approach entirely.
            </p>
            <p className="text-white/70 leading-relaxed">
              But false confidence—based on insufficient data—is even more dangerous. It leads to 
              over-leveraging, ignoring risk management, and eventually catastrophic losses when 
              reality doesn't match expectations.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-[#00E676]/10 to-purple-500/10 border border-[#00E676]/30 rounded-lg p-8">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Lightbulb size={22} weight="duotone" className="text-[#00E676]" />
              What Proper Validation Provides
            </h3>
            <div className="space-y-3">
              {[
                "Trust in your system during inevitable losing streaks",
                "Reduced emotional decision-making under pressure",
                "Ability to focus on execution rather than doubting the strategy",
                "Clear criteria for when to adjust vs. when to stay the course",
                "Long-term perspective that survives short-term volatility"
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle size={18} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                  <p className="text-white/70 leading-relaxed">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-14">
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/30 rounded-lg p-8">
            <h3 className="text-xl font-bold mb-4">Final Thoughts</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              Validating a strategy takes time, consistency, and enough data to make decisions 
              based on evidence instead of emotion. There's no shortcut. Fifty trades aren't 
              enough. Market conditions matter. Asset differences matter.
            </p>
            <p className="text-white/70 leading-relaxed">
              The traders who build lasting success are the ones who resist the urge to 
              judge too quickly—whether that judgment is positive or negative. They track 
              everything, improve their <Link to="/improve-risk-reward-ratio" className="text-[#00E676] hover:underline">risk reward ratio</Link>, 
              analyze objectively, and let the data guide decisions.
            </p>
          </div>
        </section>

        {/* Internal Links */}
        <section className="mb-14">
          <div className="p-4 border-l-2 border-[#00E676] bg-[#00E676]/5 rounded-r-lg">
            <p className="text-white/80 text-sm mb-3">
              <strong>Related Articles:</strong>
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              <Link to="/how-to-find-a-trading-setup" className="text-[#00E676] hover:underline text-sm">
                How to Find a Trading Setup →
              </Link>
              <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline text-sm">
                How to Analyze Your Trades →
              </Link>
              <Link to="/improve-risk-reward-ratio" className="text-[#00E676] hover:underline text-sm">
                Improve Risk Reward Ratio →
              </Link>
              <Link to="/why-traders-fail-to-improve" className="text-[#00E676] hover:underline text-sm">
                Why Traders Fail to Improve →
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="mb-14">
          <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/how-to-find-a-trading-setup" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Find a Trading Setup</h3>
              <p className="text-white/50 text-sm">Discover what makes a profitable trading setup.</p>
            </Link>
            <Link to="/how-to-analyze-your-trades" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Analyze Your Trades</h3>
              <p className="text-white/50 text-sm">Step-by-step guide to reviewing trades like a pro.</p>
            </Link>
            <Link to="/improve-risk-reward-ratio" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Improve Risk Reward Ratio</h3>
              <p className="text-white/50 text-sm">Better R:R through execution and psychology.</p>
            </Link>
            <Link to="/automated-trading-journal" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">What Is an Automated Journal?</h3>
              <p className="text-white/50 text-sm">Track trades automatically for better data.</p>
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Question size={28} weight="duotone" className="text-purple-400" />
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <CaretDown 
                    size={18} 
                    className={`text-white/40 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Soft CTA */}
        <section>
          <div className="bg-gradient-to-br from-[#00E676]/10 via-[#121212] to-purple-500/10 border border-white/10 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <ChartBar size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Build Confidence Through Data
            </h2>
            
            <p className="text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
              Proper validation requires tracking every trade with consistent metrics. 
              The more data you collect, the clearer your edge becomes.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Start Tracking Free
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Learn More
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              No credit card required · Free tier available
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "How Many Trades Do You Need to Validate a Trading Strategy?",
              "description": "Learn how many trades you need to validate a trading strategy and avoid common mistakes caused by small sample sizes.",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0],
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://genitradeapp.com/how-many-trades-to-validate-a-strategy"
              }
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </article>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowManyTradesToValidate;
